import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { usePermission } from '@/permissions/usePermission';
import { TenantPermissions } from './permissions';
import { useTenant } from './api';
import { ClientInfoTab } from './edit/ClientInfoTab';
import { ReportsTab } from './edit/ReportsTab';
import { ContactDetailsTab } from './edit/ContactDetailsTab';
import { ConnectionStringsTab } from './edit/ConnectionStringsTab';
import { WhiteListedIpTab } from './edit/WhiteListedIpTab';
import { RtExperienceTab } from './edit/RtExperienceTab';
import { JobTogglesTab } from './edit/JobTogglesTab';
import { SubscribedProductsTab } from './edit/SubscribedProductsTab';
import { ConnectorsTab } from './edit/ConnectorsTab';
import { Tabs } from '@/components/common/Tabs';

type TabKey =
  | 'client-info'
  | 'reports-information'
  | 'contact-details'
  | 'subscribed-products'
  | 'connectors'
  | 'connection-strings'
  | 'whitelisted-ips'
  | 'rt-experience'
  | 'job-toggles';

interface TabDef {
  key: TabKey;
  title: string;
  permission?: string;
}

// Order mirrors the legacy finkit-tabs strip in Edit/Index.cshtml.
const TABS: TabDef[] = [
  { key: 'client-info', title: 'Client Info' },
  { key: 'reports-information', title: 'Reports Information' },
  { key: 'contact-details', title: 'Contact Details' },
  { key: 'subscribed-products', title: 'Subscribed Products' },
  { key: 'connectors', title: 'Connectors' },
  {
    key: 'connection-strings',
    title: 'Connection Strings',
    permission: TenantPermissions.manageConnectionStrings,
  },
  { key: 'whitelisted-ips', title: 'WhiteListed IPs' },
  { key: 'rt-experience', title: 'RT Experience' },
  { key: 'job-toggles', title: 'Syncs & Jobs' },
];

export function TenantEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const can = usePermission();
  const tenantQuery = useTenant(id);
  const [active, setActive] = useState<TabKey>('client-info');

  const visibleTabs = TABS.filter((t) => can(t.permission));

  if (!id) return <ErrorState message="Invalid tenant id." />;
  if (tenantQuery.isLoading) return <Spinner />;
  if (tenantQuery.isError)
    return <ErrorState message="Tenant not found." onRetry={() => tenantQuery.refetch()} />;

  const goToList = () => navigate('/host/tenants');

  return (
    <div id="tabs-edit-root">
      <PageHeader
        title={`Edit Client - ${tenantQuery.data?.name ?? ''}`}
        breadcrumb={['Clients', 'Edit Client']}
      />

      <div className="card custom-card border-0 shadow-none">
        <div className="card-header bg-light mb-1">
          <div className="card-title">Edit Clients</div>
        </div>

        <Tabs
          className="mb-3"
          tabs={visibleTabs.map((t) => ({ key: t.key, title: t.title }))}
          active={active}
          onChange={(k) => setActive(k as TabKey)}
        />

        <div className="tab-content text-muted px-3 pb-3">
          {active === 'client-info' && <ClientInfoTab tenantId={id} onCancel={goToList} />}
          {active === 'reports-information' && <ReportsTab tenantId={id} onCancel={goToList} />}
          {active === 'contact-details' && <ContactDetailsTab tenantId={id} onCancel={goToList} />}
          {active === 'subscribed-products' && <SubscribedProductsTab tenantId={id} />}
          {active === 'connectors' && <ConnectorsTab tenantId={id} />}
          {active === 'connection-strings' && <ConnectionStringsTab tenantId={id} />}
          {active === 'whitelisted-ips' && <WhiteListedIpTab tenantId={id} />}
          {active === 'rt-experience' && <RtExperienceTab tenantId={id} onCancel={goToList} />}
          {active === 'job-toggles' && <JobTogglesTab tenantId={id} />}
        </div>
      </div>
    </div>
  );
}
